<script setup lang="ts">
const props = defineProps({
  show: { type: Boolean, required: true },
  disableTapBackground: { type: Boolean, default: false }
})

const emit = defineEmits(['showPopup', 'closePopup'])

const tapBackground = () => {
  if (!props.disableTapBackground) emit('closePopup')
}
</script>

<template>
  <!-- <Teleport to="body"> -->
  <div v-if="show" :class="`popup-screen ${show ? 'show' : ''}`">
    <div class="popup-screen-bg" @click="tapBackground"/>

    <div class="popup-screen-container">
      <div :class="`popup-screen-close ${disableTapBackground ? 'popup-screen-close-line' : ''}`">
        <div
          v-if="disableTapBackground"
          class="popup-screen-close-link clickable"
          @click="$emit('closePopup')"
        >
          <span>Cancel</span>
        </div>

        <div v-else class="popup-screen-close-btn clickable" @click="$emit('closePopup')">
          <CommonIconClose />
        </div>
      </div>

      <div v-if="show" class="popup-screen-content hide-scrollbar">
        <slot />
      </div>
    </div>
  </div>
  <!-- </Teleport> -->
</template>

<style lang="scss" scoped>
.popup-screen {
  $filter: 3px;
  $screenHeight: 65dvh;
  $screenWidth: 950px;
  $screenPadding: 60px;
  $screenBorderRadius: 20px;

  position: fixed;
  top: 0;
  left: 0;
  height: 0;
  width: 100dvw;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 200;
  overflow: hidden;
  user-select: none;
  -webkit-backdrop-filter: blur($filter);
  backdrop-filter: blur($filter);
  display: flex;
  align-items: center;
  justify-content: center;

  &-bg {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }

  &-close {
    $btnPadding: 20px;

    display: flex;
    justify-content: flex-end;
    padding: $btnPadding $btnPadding 10px;

    &-link {
      font-size: 0.95rem;
      font-weight: 500;
      color: var(--purple);
      margin-right: 25px;
      margin-bottom: 10px;
      margin-top: 5px;
    }

    &-btn {
      $size: 30px;
      height: $size;
      width: $size;
      background-color: var(--purple);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.3s ease-out;

      svg {
        $iconSize: 45%;
        height: $iconSize;
        width: $iconSize;
        fill: white;
      }
    }

    &-line {
      border-bottom: 1px solid gainsboro;
    }
  }

  &-container {
    position: relative;
    background: white;
    border-radius: $screenBorderRadius;
    z-index: 110;
    display: flex;
    flex-direction: column;
    isolation: isolate;
    animation: appear 0.3s ease-out;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  &-content {
    flex: 1;
    padding: 0 $screenPadding;
    overflow-y: scroll;
  }
}

.show {
  height: 100dvh;
  user-select: auto;
}

@media (max-width: 1100px) {
  .popup-screen {
    $screenWidth: calc(100dvw - 130px);
    $screenPadding: 50px;

    &-container {
      width: $screenWidth;
    }

    &-content {
      padding: 0 $screenPadding;
    }
  }
}

@media (max-width: 900px) {
  .popup-screen {
    align-items: flex-end;
    opacity: 0;
    height: 100dvh;
    transition: opacity 0.3s ease-out;
    pointer-events: none;

    &-container {
      width: 100dvw;
      border-radius: 0;
      max-height: 80dvh;
      transition: bottom 0.3s ease-out;
      transform: scale(1);
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      box-shadow: 0 -10px 20px rgba(0, 0, 0, 0.05);
      animation: appearUp 0.3s ease-out;
    }

    &-content {
      padding: 0 40px 40px;
    }

    &-close {
      padding: 15px 15px 10px;
    }
  }

  .show {
    opacity: 1;
    pointer-events: auto;

    .popup-screen {
      &-container {
        bottom: 0;
      }
    }
  }
}

@media (max-width: 600px) {
  .popup-screen {
    background-color: rgba(100, 100, 100, 0.5);

    &-container {
      max-height: 85dvh;
    }

    &-content {
      padding: 0 20px 30px;
    }

    &-close {
      padding: 12px;

      &-link {
        margin-right: 10px;
        margin-top: 10px;
      }

      &-btn {
        $size: 27px;
        height: $size;
        width: $size;
      }
    }
  }
  .show {
    opacity: 1;
    pointer-events: auto;

    .popup-screen {
      &-container {
        bottom: 0;
      }

      &-close {
        top: 15dvh;
      }
    }
  }
}

@media (hover: hover) {
  .popup-screen {
    &-close-btn:hover {
      $filter: 10px;

      background-color: var(--black);
      -webkit-backdrop-filter: blur($filter);
      backdrop-filter: blur($filter);
    }
  }
}

@keyframes appear {
  0% {
    transform: scale(0.95);
  }
  50% {
    transform: scale(1.01);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes appearUp {
  0% {
    transform: translateY(70px);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
